import { usePageMeta } from "@/lib/pageMeta";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { EmptyState } from "@/components/EmptyState";
import { PerformanceChart, RangeSelector, AllocationDonut, AllocationLegend } from "@/components/Charts";
import { SubPortfolioSelect } from "@/components/SubPortfolioSelect";
import { TransactionDialog } from "@/components/TransactionDialog";
import { MoversCard, RecentTransactionsCard } from "@/components/dashboard/DashboardCards";
import { PortfolioList } from "@/components/dashboard/PortfolioList";
import { useHideMoneyMarket, HideMMSwitch } from "@/components/HideMMSwitch";
import { MarketLists } from "@/components/dashboard/MarketLists";
import { SubPortfolioHeatmap } from "@/components/dashboard/SubPortfolioHeatmap";
import { usePrivacy } from "@/context/PrivacyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtCurrency, fmtSigned, fmtPct, fmtDate, gainClass } from "@/lib/format";
import { useMarketColors, dayClass } from "@/lib/marketHours";
import { Plus, PieChart } from "lucide-react";

export default function Dashboard() {
    const { selectedSub, setSelectedSub, refreshSubs } = usePortfolio();
    const { mask } = usePrivacy();
    const [range, setRange] = useState("1W");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [moverMode, setMoverMode] = useState("moves");
    const [moverTf, setMoverTf] = useState("1D");
    const [moverCount, setMoverCount] = useState(10);
    const [moverSub, setMoverSub] = useState("ALL");
    const qc = useQueryClient();
    const navigate = useNavigate();
    const dayOn = useMarketColors();

    const summary = useQuery({ queryKey: ["summary", selectedSub], queryFn: async () => (await api.get(`/portfolio/summary?sub=${selectedSub}`)).data });
    const perf = useQuery({ queryKey: ["perf", selectedSub, range], queryFn: async () => (await api.get(`/portfolio/performance?range=${range}&sub=${selectedSub}`)).data });
    const holdings = useQuery({ queryKey: ["holdings", selectedSub], queryFn: async () => (await api.get(`/portfolio/holdings?sub=${selectedSub}`)).data });
    const txns = useQuery({ queryKey: ["recent-txns", selectedSub], queryFn: async () => (await api.get(`/transactions?sub_portfolio=${selectedSub}`)).data });
    const markets = useQuery({ queryKey: ["markets"], queryFn: async () => (await api.get("/market/metrics")).data, refetchInterval: 60000 });
    const ytd = useQuery({ queryKey: ["dash-ytd", selectedSub], queryFn: async () => (await api.get(`/portfolio/performance?range=YTD&sub=${selectedSub}`)).data });
    const hasMM = (summary.data?.money_market_subs?.length ?? 0) > 0;
    const [hideMM, setHideMM] = useHideMoneyMarket();
    const allocEx = hasMM && hideMM;
    const alloc = useQuery({ queryKey: ["alloc", selectedSub, allocEx, summary.data?.money_market_subs?.join("|")], queryFn: async () => (await api.get(`/portfolio/allocation?sub=${selectedSub}${allocEx ? "&exclude_money_market=true" : ""}`)).data });
    const ytdEx = useQuery({ queryKey: ["dash-ytd-ex-mm", selectedSub, summary.data?.money_market_subs?.join("|")], enabled: hasMM, queryFn: async () => (await api.get(`/portfolio/performance?range=YTD&sub=${selectedSub}&exclude_money_market=true`)).data });
    const movers = useQuery({ queryKey: ["top-movers", moverMode, moverTf, moverCount, moverSub], queryFn: async () => (await api.get(`/portfolio/top-movers?limit=${moverCount}&mode=${moverMode}&timeframe=${moverTf}&sub_portfolio=${encodeURIComponent(moverSub)}`)).data });

    const refetchAll = () => qc.invalidateQueries();
    const s = summary.data;
    const ytdM = ytd.data?.metrics;
    const ytdExM = hasMM ? ytdEx.data?.metrics : null;
    const empty = summary.isSuccess && s?.holdings_count === 0 && (txns.data?.length ?? 0) === 0;

    usePageMeta(s ? `As of ${fmtDate(s.as_of)} · ${s.source}` : "Loading…", undefined, summary.dataUpdatedAt);
    if (summary.isError) {
        const e = summary.error;
        const status = e?.response?.status;
        const dataBody = e?.response?.data;
        const bodyStr = typeof dataBody === "string" ? dataBody : dataBody ? JSON.stringify(dataBody) : "";
        const diag = [status ? `HTTP ${status}` : (e?.code || "no response"), e?.message, bodyStr].filter(Boolean).join(" · ");
        return (
            <Card className="mt-4 p-8 text-center space-y-3" data-testid="dashboard-summary-error">
                <h2 className="font-heading text-lg font-semibold">Couldn't load your portfolio</h2>
                <p className="text-sm text-muted-foreground">We hit a problem fetching your portfolio summary. This is usually temporary.</p>
                <pre className="text-left text-[11px] text-muted-foreground bg-muted/50 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap" data-testid="dashboard-summary-error-detail">{diag}</pre>
                <Button onClick={() => { summary.refetch(); refetchAll(); }} data-testid="dashboard-summary-retry"><Plus className="w-4 h-4 mr-1.5 rotate-45" />Retry</Button>
            </Card>
        );
    }

    if (empty) {
        return (
            <>
                <Card className="mt-4">
                    <EmptyState icon={PieChart} title="Your portfolio is empty"
                        description="Add your first transaction to start tracking holdings, performance and allocation."
                        actionLabel="Add your first transaction" onAction={() => setDialogOpen(true)} testId="dashboard-empty" />
                </Card>
                <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={() => { refreshSubs(); refetchAll(); }} />
            </>
        );
    }

    // group holdings by sub-portfolio, sorted by value desc
    const grouped = {};
    (holdings.data?.holdings || []).forEach((h) => { (grouped[h.sub_portfolio] ||= []).push(h); });
    Object.values(grouped).forEach((arr) => arr.sort((a, b) => (b.market_value || 0) - (a.market_value || 0)));
    const moverRows = movers.data?.movers || [];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <SubPortfolioSelect value={selectedSub} onChange={setSelectedSub} />
                    <Button onClick={() => setDialogOpen(true)} data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-1.5" />Add Transaction</Button>
                </div>
            </div>

            {/* Headline figures */}
            {(summary.isLoading || !s) ? <Skeleton className="h-[120px] rounded-xl" /> : (() => {
                const pv = dayClass(s.day_change, dayOn);
                const exClass = dayClass(s.ex_mm_day_change, dayOn);
                const mmNote = s.money_market_subs?.length ? `excl. ${s.money_market_subs.join(", ")}` : "no money market set";
                return (
                    <PortfolioList columns={[
                        [
                            { testId: "stat-holdings-value", label: "Portfolio Value", note: "excl. cash", value: mask(fmtCurrency(s.market_value)), pct: s.market_value - s.day_change ? fmtPct((s.day_change / (s.market_value - s.day_change)) * 100) : "", pctClass: pv },
                            { testId: "stat-ex-mm-value", label: "Ex-Money Market", note: mmNote, value: mask(fmtCurrency(s.ex_mm_value)), pct: fmtPct(s.ex_mm_day_change_pct), pctClass: exClass },
                            { testId: "stat-portfolio-value", label: "Net Portfolio Value", note: "incl. cash", value: mask(fmtCurrency(s.portfolio_value)), pct: fmtPct(s.day_change_pct), pctClass: pv },
                        ],
                        [
                            { testId: "stat-today", label: "Today", value: mask(fmtSigned(s.day_change)), valueClass: pv, pct: fmtPct(s.day_change_pct), pctClass: pv },
                            { testId: "stat-ex-mm-today", label: "Today ex-Money Market", value: mask(fmtSigned(s.ex_mm_day_change)), valueClass: exClass, pct: fmtPct(s.ex_mm_day_change_pct), pctClass: exClass },
                            { testId: "stat-ytd-return", label: "YTD Return", value: ytdM ? mask(fmtSigned(ytdM.abs_change)) : "—", valueClass: gainClass(ytdM?.abs_change), pct: ytdM ? fmtPct(ytdM.pct_change) : "", pctClass: gainClass(ytdM?.abs_change) },
                            { testId: "stat-ytd-ex-mm", label: "YTD ex-Money Market", value: !hasMM ? "—" : ytdExM ? mask(fmtSigned(ytdExM.abs_change)) : "…", valueClass: gainClass(ytdExM?.abs_change), pct: ytdExM ? fmtPct(ytdExM.pct_change) : "", pctClass: gainClass(ytdExM?.abs_change) },
                        ],
                        [
                            { testId: "stat-invested", label: "Book Value", note: `${s.holdings_count} positions`, value: mask(fmtCurrency(s.invested)) },
                            { testId: "stat-cash", label: "Cash", note: "available", value: mask(fmtCurrency(s.cash)) },
                        ],
                    ]} />
                );
            })()}

            {markets.data && <MarketLists data={markets.data} onOpen={(k) => navigate(`/metric/${k}`)} />}

            {/* Heatmap + movers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <SubPortfolioHeatmap sub={selectedSub} mask={mask} height={250} className="lg:col-span-2"
                    activeSub={moverSub} onPickSub={setMoverSub} />
                <MoversCard mode={moverMode} setMode={setMoverMode} tf={moverTf} setTf={setMoverTf}
                    count={moverCount} setCount={setMoverCount} sub={moverSub} setSub={setMoverSub}
                    rows={moverRows} loading={movers.isLoading} mask={mask} onNavigate={navigate} />
            </div>

            {/* Chart + recent txns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <h2 className="font-heading text-base font-semibold">Portfolio Value</h2>
                        <RangeSelector value={range} onChange={setRange} />
                    </div>
                    {perf.isLoading ? <Skeleton className="h-[220px] rounded-lg" /> : <PerformanceChart series={perf.data?.series || []} height={220} />}
                </Card>
                <RecentTransactionsCard txns={txns.data} mask={mask} onViewAll={() => navigate("/transactions")} />
            </div>

            {/* Allocation */}
            <Card className="p-4" data-testid="dashboard-allocation-card">
                <div className="flex items-center justify-between gap-3">
                    <h2 className="font-heading text-base font-semibold">Allocation <span className="text-xs font-normal text-muted-foreground">· by sub-portfolio</span></h2>
                    {hasMM && <HideMMSwitch checked={hideMM} onChange={setHideMM} testId="dashboard-hide-mm-switch" />}
                </div>
                {alloc.isLoading ? <Skeleton className="h-[150px] rounded-lg mt-2" /> : (
                    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-center gap-4 mt-1">
                        <div data-testid="dashboard-allocation-chart"><AllocationDonut data={alloc.data?.by_sub_portfolio || []} height={150} /></div>
                        <AllocationLegend data={alloc.data?.by_sub_portfolio || []} />
                    </div>
                )}
            </Card>

            <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={() => { refreshSubs(); refetchAll(); }} />
        </div>
    );
}
